import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FolderOpen, Plus, Loader2, X, AlertTriangle, CheckCircle,
  Clock, MessageSquare, FileText, ChevronRight,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'

const PRIORITY_COLOURS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-800',
  critical: 'bg-red-100 text-red-800',
}

export function CasesPage() {
  const { currentPropertyId } = useAuthStore()
  const queryClient = useQueryClient()

  const [filterStatus, setFilterStatus] = useState('')
  const [filterType, setFilterType] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedCase, setSelectedCase] = useState<any | null>(null)
  const [newComment, setNewComment] = useState('')

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: cases, isLoading } = useQuery({
    queryKey: ['docflow-cases', filterStatus, filterType],
    queryFn: () => apiGet<any>('/docflow/cases', {
      ...(filterStatus && { status: filterStatus }),
      ...(filterType && { case_type_id: filterType }),
    }),
  })

  const { data: caseTypes } = useQuery({
    queryKey: ['docflow-case-types'],
    queryFn: () => apiGet<any[]>('/docflow/cases/types'),
  })

  // ── Create case ────────────────────────────────────────────────────────
  const [newCase, setNewCase] = useState({
    case_type_id: '', title: '', priority: 'medium', description: '',
  })

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/docflow/cases', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-cases'] })
      setShowCreateModal(false)
      setNewCase({ case_type_id: '', title: '', priority: 'medium', description: '' })
    },
  })

  // ── Case detail ────────────────────────────────────────────────────────
  const { data: caseDetail } = useQuery({
    queryKey: ['docflow-case-detail', selectedCase?.reference],
    queryFn: () => apiGet<any>(`/docflow/cases/${selectedCase.reference}`),
    enabled: !!selectedCase?.reference,
  })

  const commentMutation = useMutation({
    mutationFn: (content: string) =>
      apiPost(`/docflow/cases/${selectedCase?.reference}/comments`, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-case-detail'] })
      setNewComment('')
    },
  })

  const closeMutation = useMutation({
    mutationFn: (reason: string) =>
      apiPost(`/docflow/cases/${selectedCase?.reference}/close`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docflow-cases'] })
      queryClient.invalidateQueries({ queryKey: ['docflow-case-detail'] })
    },
  })

  const caseList = cases?.cases ?? (Array.isArray(cases) ? cases : [])
  const openCount = caseList.filter((c: any) => c.status === 'open').length
  const closedCount = caseList.filter((c: any) => c.status === 'closed').length
  const types = Array.isArray(caseTypes) ? caseTypes : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Cases</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Maintenance requests, incidents, complaints, and investigations
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> New Case
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card py-4">
          <p className="text-xs text-gray-400">Total Cases</p>
          <p className="text-2xl font-bold text-navy-800">{caseList.length}</p>
        </div>
        <div className="card py-4 border-blue-200">
          <p className="text-xs text-gray-400">Open</p>
          <p className="text-2xl font-bold text-blue-600">{openCount}</p>
        </div>
        <div className="card py-4 border-green-200">
          <p className="text-xs text-gray-400">Closed</p>
          <p className="text-2xl font-bold text-green-600">{closedCount}</p>
        </div>
        <div className="card py-4 border-amber-200">
          <p className="text-xs text-gray-400">Case Types</p>
          <p className="text-2xl font-bold text-navy-800">{types.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="input w-auto min-w-[140px]"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="closed">Closed</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="input w-auto min-w-[180px]"
        >
          <option value="">All Types</option>
          {types.map((t: any) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Case List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-800" />
        </div>
      ) : caseList.length === 0 ? (
        <div className="card text-center py-16">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No cases found</p>
          <p className="text-xs text-gray-400 mt-1">Create a case to track incidents, complaints, or maintenance requests</p>
        </div>
      ) : (
        <div className="space-y-2">
          {caseList.map((c: any) => (
            <button
              key={c.id ?? c.reference}
              onClick={() => setSelectedCase(c)}
              className="card w-full text-left hover:border-navy-300 transition-colors flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono font-bold text-navy-600">{c.reference}</span>
                  <span className={cn('badge text-xs', PRIORITY_COLOURS[c.priority] ?? 'bg-gray-100 text-gray-700')}>
                    {c.priority}
                  </span>
                  {c.status === 'closed' ? (
                    <span className="badge bg-green-100 text-green-700 text-xs">Closed</span>
                  ) : (
                    <span className="badge bg-blue-100 text-blue-700 text-xs">{c.status?.replace('_', ' ')}</span>
                  )}
                </div>
                <p className="text-sm font-medium text-navy-800 truncate">{c.title}</p>
                {c.description && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{c.description}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-gray-400">{formatDate(c.created_at)}</p>
                {c.case_type_name && (
                  <p className="text-xs text-gray-500 mt-0.5">{c.case_type_name}</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Case Detail Slide-over */}
      {selectedCase && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="bg-white w-full max-w-lg shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                  <FolderOpen className="w-5 h-5 text-blue-600" />
                  {selectedCase.reference}
                </h2>
                <p className="text-sm text-gray-500">{selectedCase.title}</p>
              </div>
              <button onClick={() => setSelectedCase(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Status & Priority */}
              <div className="flex gap-2">
                <span className={cn('badge', PRIORITY_COLOURS[selectedCase.priority] ?? 'bg-gray-100 text-gray-700')}>
                  {selectedCase.priority}
                </span>
                <span className={cn('badge',
                  selectedCase.status === 'closed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                )}>
                  {selectedCase.status?.replace('_', ' ')}
                </span>
              </div>

              {/* Description */}
              {(caseDetail?.description || selectedCase.description) && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</h3>
                  <p className="text-sm text-navy-800">{caseDetail?.description || selectedCase.description}</p>
                </div>
              )}

              {/* Tasks */}
              {caseDetail?.tasks?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Tasks</h3>
                  <div className="space-y-1">
                    {caseDetail.tasks.map((task: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        {task.completed ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-gray-400" />
                        )}
                        <span className={cn(task.completed && 'line-through text-gray-400')}>{task.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {caseDetail?.documents?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Documents</h3>
                  <div className="space-y-1">
                    {caseDetail.documents.map((doc: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-navy-800">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span>{doc.title ?? doc.filename ?? doc.document_id}</span>
                        {doc.role && <span className="badge text-xs bg-gray-100 text-gray-600">{doc.role}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments */}
              {caseDetail?.comments?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Comments</h3>
                  <div className="space-y-2">
                    {caseDetail.comments.map((comment: any, i: number) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3">
                        <p className="text-sm text-navy-800">{comment.content}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {comment.author_name ?? 'System'} &middot; {formatDate(comment.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add comment */}
              {selectedCase.status !== 'closed' && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Add Comment</h3>
                  <div className="flex gap-2">
                    <input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a comment..."
                      className="input flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newComment.trim()) commentMutation.mutate(newComment)
                      }}
                    />
                    <button
                      onClick={() => newComment.trim() && commentMutation.mutate(newComment)}
                      disabled={!newComment.trim() || commentMutation.isPending}
                      className="btn-primary"
                    >
                      {commentMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer actions */}
            {selectedCase.status !== 'closed' && (
              <div className="px-6 py-4 border-t flex justify-end">
                <button
                  onClick={() => {
                    const reason = prompt('Close reason:')
                    if (reason) closeMutation.mutate(reason)
                  }}
                  disabled={closeMutation.isPending}
                  className="btn-secondary text-sm"
                >
                  {closeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Close Case
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Case Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800 flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-blue-600" />
                New Case
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!newCase.case_type_id || !newCase.title) return
                createMutation.mutate(newCase)
              }}
              className="space-y-4"
            >
              <div>
                <label className="label">Case Type *</label>
                <select
                  value={newCase.case_type_id}
                  onChange={(e) => setNewCase({ ...newCase, case_type_id: e.target.value })}
                  className="input"
                  required
                >
                  <option value="">Select type...</option>
                  {types.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.prefix} — {t.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Title *</label>
                <input
                  value={newCase.title}
                  onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                  className="input"
                  placeholder="Brief description of the case"
                  required
                />
              </div>

              <div>
                <label className="label">Priority</label>
                <select
                  value={newCase.priority}
                  onChange={(e) => setNewCase({ ...newCase, priority: e.target.value })}
                  className="input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  value={newCase.description}
                  onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                  rows={3}
                  className="input resize-none"
                  placeholder="Full details..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newCase.case_type_id || !newCase.title || createMutation.isPending}
                  className="btn-primary"
                >
                  {createMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Create Case</>
                  )}
                </button>
              </div>

              {createMutation.isError && (
                <p className="text-sm text-red-600">Failed to create case. Check DocFlow connection.</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
